import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useCoin } from '../CoinContext';
import Web3Login from '../components/Web3Login';
import CampChatListModal from '../components/CampChatListModal';

const SERVERS = ['Ain', 'Fang', 'Errai'];

const BOSSES = [
  'Lucid (Easy)', 'Lucid (Normal)', 'Lucid (Hard)',
  'Guardian Angel Slime',
  'Damien (Normal)', 'Damien (Hard)',
  'Lotus (Normal)', 'Lotus (Hard)',
  'Papulatus (Easy)', 'Papulatus (Normal)', 'Papulatus (Chaos)',
  'Cygnus (Easy)', 'Cygnus (Normal)',
  'Pink Bean (Normal)', 'Pink Bean (Chaos)',
  'Magnus (Easy)', 'Magnus (Normal)', 'Magnus (Hard)',
  'Arkarium (Easy)', 'Arkarium (Normal)',
  'OMNI-CLN',
  'Von Leon (Easy)', 'Von Leon (Normal)', 'Von Leon (Hard)',
  'Vellum (Normal)', 'Vellum (Chaos)',
  'Crimson Queen (Normal)', 'Crimson Queen (Chaos)',
  'Von Bon (Normal)', 'Von Bon (Chaos)',
  'Pierre (Normal)', 'Pierre (Chaos)',
  'Hilla (Normal)', 'Hilla (Hard)',
  'Horntail (Easy)', 'Horntail (Normal)', 'Horntail (Chaos)',
  'Zakum (Easy)', 'Zakum (Normal)', 'Zakum (Chaos)',
  'Balrog',
];

const LANGUAGES = [
  'English', 'Korean', 'Japanese', 'Chinese (Traditional)', 'Chinese (Simplified)',
  'Thai', 'Vietnamese', 'Indonesian', 'Malay', 'Filipino', 'Spanish', 'Portuguese',
];

const UI_LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'ko', label: '한국어' },
  { code: 'ja', label: '日本語' },
  { code: 'zh-TW', label: '繁體中文' },
  { code: 'zh-CN', label: '简体中文' },
  { code: 'th', label: 'ไทย' },
  { code: 'vi', label: 'Tiếng Việt' },
  { code: 'id', label: 'Bahasa Indonesia' },
  { code: 'ms', label: 'Bahasa Melayu' },
  { code: 'fil', label: 'Filipino' },
  { code: 'es', label: 'Español' },
  { code: 'pt', label: 'Português' },
];

const TRANSLATIONS = {
  en: {
    camp: 'Camp',
    selectLanguage: 'Select Language',
    filter: 'Filter',
    write: 'Write',
    search: 'Search...',
    notice: 'Notice',
    allServers: 'All Servers',
    allBosses: 'All Bosses',
    allLanguages: 'All Languages',
    clearFilters: 'Clear Filters',
    loading: 'Loading...',
    noPostsFilter: 'No posts match your filters.',
    noPostsSearch: 'No posts match your search.',
    noPostsYet: 'No posts yet. Be the first to write!',
    anonymous: 'Anonymous',
    justNow: 'Just now',
    mAgo: 'm ago',
    hAgo: 'h ago',
    dAgo: 'd ago',
    login: 'Login',
    logout: 'Logout',
    loginToWrite: 'Login to write',
    settings: 'Settings',
    theme: 'Theme',
    themeSystem: 'System',
    themeLight: 'Light',
    themeDark: 'Dark',
    myPage: 'My Page',
    myPosts: 'My Posts',
    noMyPosts: 'You have no posts yet.',
  },
  ko: {
    camp: '캠프',
    selectLanguage: '언어 선택',
    filter: '필터',
    write: '글쓰기',
    search: '검색...',
    notice: '공지',
    allServers: '전체 서버',
    allBosses: '전체 보스',
    allLanguages: '전체 언어',
    clearFilters: '필터 초기화',
    loading: '로딩 중...',
    noPostsFilter: '필터와 일치하는 글이 없습니다.',
    noPostsSearch: '검색 결과가 없습니다.',
    noPostsYet: '아직 글이 없습니다. 첫 글을 작성해보세요!',
    anonymous: '익명',
    justNow: '방금 전',
    mAgo: '분 전',
    hAgo: '시간 전',
    dAgo: '일 전',
    login: '로그인',
    logout: '로그아웃',
    loginToWrite: '글쓰기는 로그인이 필요합니다',
    settings: '설정',
    theme: '테마',
    themeSystem: '시스템',
    themeLight: '라이트',
    themeDark: '다크',
    myPage: '마이페이지',
    myPosts: '내 글',
    noMyPosts: '작성한 글이 없습니다.',
  },
  ja: {
    camp: 'キャンプ',
    selectLanguage: '言語を選択',
    filter: 'フィルター',
    write: '投稿',
    search: '検索...',
    notice: 'お知らせ',
    allServers: '全サーバー',
    allBosses: '全ボス',
    allLanguages: '全言語',
    clearFilters: 'フィルターをクリア',
    loading: '読み込み中...',
    noPostsFilter: 'フィルターに一致する投稿がありません。',
    noPostsSearch: '検索結果がありません。',
    noPostsYet: 'まだ投稿がありません。最初の投稿をしましょう！',
    anonymous: '匿名',
    justNow: 'たった今',
    mAgo: '分前',
    hAgo: '時間前',
    dAgo: '日前',
    login: 'ログイン',
    logout: 'ログアウト',
    loginToWrite: '投稿にはログインが必要です',
    settings: '設定',
    theme: 'テーマ',
    themeSystem: 'システム',
    themeLight: 'ライト',
    themeDark: 'ダーク',
    myPage: 'マイページ',
    myPosts: '自分の投稿',
    noMyPosts: 'まだ投稿がありません。',
  },
  'zh-TW': {
    camp: '營地',
    selectLanguage: '選擇語言',
    filter: '篩選',
    write: '發文',
    search: '搜尋...',
    notice: '公告',
    allServers: '所有伺服器',
    allBosses: '所有首領',
    allLanguages: '所有語言',
    clearFilters: '清除篩選',
    loading: '載入中...',
    noPostsFilter: '沒有符合篩選條件的文章。',
    noPostsSearch: '沒有符合搜尋條件的文章。',
    noPostsYet: '還沒有文章。成為第一個發文的人吧！',
    anonymous: '匿名',
    justNow: '剛剛',
    mAgo: '分鐘前',
    hAgo: '小時前',
    dAgo: '天前',
    login: '登入',
    logout: '登出',
    loginToWrite: '發文需要登入',
    settings: '設定',
    theme: '主題',
    themeSystem: '系統',
    themeLight: '淺色',
    themeDark: '深色',
    myPage: '我的頁面',
    myPosts: '我的文章',
    noMyPosts: '還沒有文章。',
  },
  'zh-CN': {
    camp: '营地',
    selectLanguage: '选择语言',
    filter: '筛选',
    write: '发帖',
    search: '搜索...',
    notice: '公告',
    allServers: '所有服务器',
    allBosses: '所有首领',
    allLanguages: '所有语言',
    clearFilters: '清除筛选',
    loading: '加载中...',
    noPostsFilter: '没有符合筛选条件的帖子。',
    noPostsSearch: '没有符合搜索条件的帖子。',
    noPostsYet: '还没有帖子。成为第一个发帖的人吧！',
    anonymous: '匿名',
    justNow: '刚刚',
    mAgo: '分钟前',
    hAgo: '小时前',
    dAgo: '天前',
    login: '登录',
    logout: '登出',
    loginToWrite: '发帖需要登录',
    settings: '设置',
    theme: '主题',
    themeSystem: '系统',
    themeLight: '浅色',
    themeDark: '深色',
    myPage: '我的页面',
    myPosts: '我的帖子',
    noMyPosts: '还没有帖子。',
  },
  th: {
    camp: 'แคมป์',
    selectLanguage: 'เลือกภาษา',
    filter: 'ตัวกรอง',
    write: 'เขียน',
    search: 'ค้นหา...',
    notice: 'ประกาศ',
    allServers: 'เซิร์ฟเวอร์ทั้งหมด',
    allBosses: 'บอสทั้งหมด',
    allLanguages: 'ภาษาทั้งหมด',
    clearFilters: 'ล้างตัวกรอง',
    loading: 'กำลังโหลด...',
    noPostsFilter: 'ไม่มีโพสต์ที่ตรงกับตัวกรองของคุณ',
    noPostsSearch: 'ไม่พบโพสต์ที่ตรงกับการค้นหา',
    noPostsYet: 'ยังไม่มีโพสต์ เป็นคนแรกที่เขียนสิ!',
    anonymous: 'ไม่ระบุชื่อ',
    justNow: 'เมื่อสักครู่',
    mAgo: ' นาทีที่แล้ว',
    hAgo: ' ชั่วโมงที่แล้ว',
    dAgo: ' วันที่แล้ว',
    login: 'เข้าสู่ระบบ',
    logout: 'ออกจากระบบ',
    loginToWrite: 'เข้าสู่ระบบเพื่อเขียน',
    settings: 'ตั้งค่า',
    theme: 'ธีม',
    themeSystem: 'ระบบ',
    themeLight: 'สว่าง',
    themeDark: 'มืด',
    myPage: 'หน้าของฉัน',
    myPosts: 'โพสต์ของฉัน',
    noMyPosts: 'คุณยังไม่มีโพสต์',
  },
  vi: {
    camp: 'Trại',
    selectLanguage: 'Chọn ngôn ngữ',
    filter: 'Bộ lọc',
    write: 'Viết',
    search: 'Tìm kiếm...',
    notice: 'Thông báo',
    allServers: 'Tất cả máy chủ',
    allBosses: 'Tất cả boss',
    allLanguages: 'Tất cả ngôn ngữ',
    clearFilters: 'Xóa bộ lọc',
    loading: 'Đang tải...',
    noPostsFilter: 'Không có bài đăng nào phù hợp với bộ lọc.',
    noPostsSearch: 'Không có bài đăng nào phù hợp với tìm kiếm.',
    noPostsYet: 'Chưa có bài đăng. Hãy là người đầu tiên viết!',
    anonymous: 'Ẩn danh',
    justNow: 'Vừa xong',
    mAgo: ' phút trước',
    hAgo: ' giờ trước',
    dAgo: ' ngày trước',
    login: 'Đăng nhập',
    logout: 'Đăng xuất',
    loginToWrite: 'Đăng nhập để viết',
    settings: 'Cài đặt',
    theme: 'Giao diện',
    themeSystem: 'Hệ thống',
    themeLight: 'Sáng',
    themeDark: 'Tối',
    myPage: 'Trang của tôi',
    myPosts: 'Bài đăng của tôi',
    noMyPosts: 'Bạn chưa có bài đăng nào.',
  },
  id: {
    camp: 'Kamp',
    selectLanguage: 'Pilih Bahasa',
    filter: 'Filter',
    write: 'Tulis',
    search: 'Cari...',
    notice: 'Pengumuman',
    allServers: 'Semua Server',
    allBosses: 'Semua Bos',
    allLanguages: 'Semua Bahasa',
    clearFilters: 'Hapus Filter',
    loading: 'Memuat...',
    noPostsFilter: 'Tidak ada postingan yang sesuai filter.',
    noPostsSearch: 'Tidak ada postingan yang sesuai pencarian.',
    noPostsYet: 'Belum ada postingan. Jadilah yang pertama menulis!',
    anonymous: 'Anonim',
    justNow: 'Baru saja',
    mAgo: ' menit lalu',
    hAgo: ' jam lalu',
    dAgo: ' hari lalu',
    login: 'Masuk',
    logout: 'Keluar',
    loginToWrite: 'Masuk untuk menulis',
    settings: 'Pengaturan',
    theme: 'Tema',
    themeSystem: 'Sistem',
    themeLight: 'Terang',
    themeDark: 'Gelap',
    myPage: 'Halaman Saya',
    myPosts: 'Postingan Saya',
    noMyPosts: 'Anda belum memiliki postingan.',
  },
  ms: {
    camp: 'Kem',
    selectLanguage: 'Pilih Bahasa',
    filter: 'Tapis',
    write: 'Tulis',
    search: 'Cari...',
    notice: 'Pengumuman',
    allServers: 'Semua Pelayan',
    allBosses: 'Semua Bos',
    allLanguages: 'Semua Bahasa',
    clearFilters: 'Padam Tapisan',
    loading: 'Memuatkan...',
    noPostsFilter: 'Tiada siaran sepadan dengan tapisan anda.',
    noPostsSearch: 'Tiada siaran sepadan dengan carian anda.',
    noPostsYet: 'Belum ada siaran. Jadilah yang pertama menulis!',
    anonymous: 'Tanpa Nama',
    justNow: 'Baru sahaja',
    mAgo: ' minit lalu',
    hAgo: ' jam lalu',
    dAgo: ' hari lalu',
    login: 'Log Masuk',
    logout: 'Log Keluar',
    loginToWrite: 'Log masuk untuk menulis',
    settings: 'Tetapan',
    theme: 'Tema',
    themeSystem: 'Sistem',
    themeLight: 'Cerah',
    themeDark: 'Gelap',
    myPage: 'Halaman Saya',
    myPosts: 'Siaran Saya',
    noMyPosts: 'Anda belum mempunyai siaran.',
  },
  fil: {
    camp: 'Camp',
    selectLanguage: 'Pumili ng Wika',
    filter: 'Filter',
    write: 'Sumulat',
    search: 'Maghanap...',
    notice: 'Paunawa',
    allServers: 'Lahat ng Server',
    allBosses: 'Lahat ng Boss',
    allLanguages: 'Lahat ng Wika',
    clearFilters: 'I-clear ang Filter',
    loading: 'Naglo-load...',
    noPostsFilter: 'Walang post na tumutugma sa iyong filter.',
    noPostsSearch: 'Walang post na tumutugma sa iyong paghahanap.',
    noPostsYet: 'Wala pang post. Maging una kang magsulat!',
    anonymous: 'Anonymous',
    justNow: 'Kamakailan lang',
    mAgo: ' minuto ang nakalipas',
    hAgo: ' oras ang nakalipas',
    dAgo: ' araw ang nakalipas',
    login: 'Mag-login',
    logout: 'Mag-logout',
    loginToWrite: 'Mag-login para magsulat',
    settings: 'Mga Setting',
    theme: 'Tema',
    themeSystem: 'Sistema',
    themeLight: 'Maliwanag',
    themeDark: 'Madilim',
    myPage: 'Aking Pahina',
    myPosts: 'Aking mga Post',
    noMyPosts: 'Wala ka pang post.',
  },
  es: {
    camp: 'Campamento',
    selectLanguage: 'Seleccionar idioma',
    filter: 'Filtro',
    write: 'Escribir',
    search: 'Buscar...',
    notice: 'Aviso',
    allServers: 'Todos los servidores',
    allBosses: 'Todos los jefes',
    allLanguages: 'Todos los idiomas',
    clearFilters: 'Limpiar filtros',
    loading: 'Cargando...',
    noPostsFilter: 'No hay publicaciones que coincidan con tus filtros.',
    noPostsSearch: 'No hay publicaciones que coincidan con tu búsqueda.',
    noPostsYet: '¡Aún no hay publicaciones. Sé el primero en escribir!',
    anonymous: 'Anónimo',
    justNow: 'Ahora mismo',
    mAgo: ' min atrás',
    hAgo: ' h atrás',
    dAgo: ' d atrás',
    login: 'Iniciar sesión',
    logout: 'Cerrar sesión',
    loginToWrite: 'Inicia sesión para escribir',
    settings: 'Configuración',
    theme: 'Tema',
    themeSystem: 'Sistema',
    themeLight: 'Claro',
    themeDark: 'Oscuro',
    myPage: 'Mi Página',
    myPosts: 'Mis Publicaciones',
    noMyPosts: 'Aún no tienes publicaciones.',
  },
  pt: {
    camp: 'Acampamento',
    selectLanguage: 'Selecionar idioma',
    filter: 'Filtro',
    write: 'Escrever',
    search: 'Pesquisar...',
    notice: 'Aviso',
    allServers: 'Todos os servidores',
    allBosses: 'Todos os chefes',
    allLanguages: 'Todos os idiomas',
    clearFilters: 'Limpar filtros',
    loading: 'Carregando...',
    noPostsFilter: 'Nenhuma postagem corresponde aos seus filtros.',
    noPostsSearch: 'Nenhuma postagem corresponde à sua pesquisa.',
    noPostsYet: 'Ainda não há postagens. Seja o primeiro a escrever!',
    anonymous: 'Anônimo',
    justNow: 'Agora mesmo',
    mAgo: ' min atrás',
    hAgo: ' h atrás',
    dAgo: ' d atrás',
    login: 'Entrar',
    logout: 'Sair',
    loginToWrite: 'Faça login para escrever',
    settings: 'Configurações',
    theme: 'Tema',
    themeSystem: 'Sistema',
    themeLight: 'Claro',
    themeDark: 'Escuro',
    myPage: 'Minha Página',
    myPosts: 'Minhas Postagens',
    noMyPosts: 'Você ainda não tem postagens.',
  },
};

// Theme colors
const THEMES = {
  dark: {
    bg: '#121212',
    contentBg: 'rgba(18, 18, 18, 0.98)',
    cardBg: 'rgba(255,255,255,0.05)',
    cardHoverBg: 'rgba(255,255,255,0.08)',
    inputBg: 'rgba(255,255,255,0.06)',
    border: 'rgba(255,255,255,0.12)',
    text: '#e0e0e0',
    textSecondary: '#a0a0a0',
    textMuted: '#707070',
  },
  light: {
    bg: '#f5f5f5',
    contentBg: 'rgba(255, 255, 255, 0.98)',
    cardBg: 'rgba(0,0,0,0.03)',
    cardHoverBg: 'rgba(0,0,0,0.06)',
    inputBg: 'rgba(0,0,0,0.05)',
    border: 'rgba(0,0,0,0.1)',
    text: '#1a1a1a',
    textSecondary: '#555',
    textMuted: '#888',
  },
};

// Helper to get correct path based on subdomain
const getPath = (path) => {
  const isCampSubdomain = window.location.hostname === 'camp.ssalsoongi.com';
  if (isCampSubdomain) {
    // On subdomain, remove /camp prefix
    return path.replace(/^\/camp/, '') || '/';
  }
  return path;
};

export default function CampPage() {
  const navigate = useNavigate();
  const { walletAddress } = useCoin();
  const [posts, setPosts] = useState([]);
  const [pinnedPost, setPinnedPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterServer, setFilterServer] = useState('');
  const [filterBoss, setFilterBoss] = useState('');
  const [filterLanguage, setFilterLanguage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [hideRecruited, setHideRecruited] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [isChatListOpen, setIsChatListOpen] = useState(false);
  const [uiLanguage, setUiLanguage] = useState(() => {
    return localStorage.getItem('campUiLanguage') || 'en';
  });
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('campTheme');
    if (saved) return saved;
    // Follow system preference
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });
  const [themeMode, setThemeMode] = useState(() => {
    return localStorage.getItem('campThemeMode') || 'system'; // 'system', 'dark', 'light'
  });

  const colors = THEMES[theme];

  // Listen for system theme changes
  useEffect(() => {
    if (themeMode !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      setTheme(e.matches ? 'dark' : 'light');
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [themeMode]);

  const handleLanguageChange = (langCode) => {
    setUiLanguage(langCode);
    localStorage.setItem('campUiLanguage', langCode);
  };

  const handleThemeModeChange = (mode) => {
    setThemeMode(mode);
    localStorage.setItem('campThemeMode', mode);

    if (mode === 'system') {
      localStorage.removeItem('campTheme');
      setTheme(window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');
    } else {
      localStorage.setItem('campTheme', mode);
      setTheme(mode);
    }
  };

  const t = (key) => TRANSLATIONS[uiLanguage]?.[key] || TRANSLATIONS.en[key];

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      // Fetch pinned post (notice)
      const { data: pinnedData } = await supabase
        .from('camp_posts')
        .select('*')
        .eq('is_pinned', true)
        .limit(1)
        .maybeSingle();

      setPinnedPost(pinnedData || null);

      // Fetch regular posts (excluding pinned)
      const { data, error } = await supabase
        .from('camp_posts')
        .select('*')
        .neq('is_pinned', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPosts(data || []);
    } catch (err) {
      console.error('Failed to fetch posts:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return t('justNow');
    if (minutes < 60) return `${minutes}${t('mAgo')}`;
    if (hours < 24) return `${hours}${t('hAgo')}`;
    if (days < 7) return `${days}${t('dAgo')}`;
    return date.toLocaleDateString();
  };

  const filteredPosts = useMemo(() => {
    return posts.filter(post => {
      if (filterServer && post.server !== filterServer) return false;
      if (filterBoss && post.boss !== filterBoss) return false;
      if (filterLanguage && (!post.languages || !post.languages.includes(filterLanguage))) return false;
      if (hideRecruited) {
        const recruitSlots = (post.members || []).filter(m => m.type === 'recruit');
        const rc = post.recruited_classes || [];
        if (recruitSlots.length > 0 && recruitSlots.every(m => rc.includes(m.character_class || 'Any Class'))) return false;
      }
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const titleMatch = post.title?.toLowerCase().includes(query);
        const contentMatch = post.content?.toLowerCase().includes(query);
        const authorMatch = post.author_nickname?.toLowerCase().includes(query);
        if (!titleMatch && !contentMatch && !authorMatch) return false;
      }
      return true;
    });
  }, [posts, filterServer, filterBoss, filterLanguage, hideRecruited, searchQuery]);

  const activeFilterCount = [filterServer, filterBoss, filterLanguage, hideRecruited].filter(Boolean).length;

  const clearFilters = () => {
    setFilterServer('');
    setFilterBoss('');
    setFilterLanguage('');
    setHideRecruited(false);
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        overflow: 'hidden',
        background: colors.bg,
      }}
    >
      {/* Main Content Area */}
      <div
        style={{
          width: '100%',
          maxWidth: 430,
          minHeight: '100vh',
          background: colors.contentBg,
          padding: 20,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          position: 'relative',
          zIndex: 2,
          boxShadow: '0 0 40px rgba(0,0,0,0.3)',
        }}
      >
      {/* Header */}
      <div
        style={{
          width: '100%',
          maxWidth: 430,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 20,
        }}
      >
        <h1
          style={{
            margin: 0,
            fontSize: 24,
            fontWeight: 700,
            color: colors.text,
          }}
        >
          {t('camp')}
        </h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {walletAddress ? (
            <>
              <button
                onClick={() => navigate(getPath('/camp/write'))}
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  borderRadius: 10,
                  padding: '8px 14px',
                  cursor: 'pointer',
                  color: '#fff',
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                {t('write')}
              </button>
              <button
                onClick={() => setIsChatListOpen(true)}
                style={{
                  background: colors.inputBg,
                  border: `1px solid ${colors.border}`,
                  borderRadius: 10,
                  padding: '8px 10px',
                  cursor: 'pointer',
                  color: colors.textSecondary,
                  fontSize: 16,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                💬
              </button>
              <button
                onClick={() => navigate(getPath('/camp/my'))}
                style={{
                  background: colors.inputBg,
                  border: `1px solid ${colors.border}`,
                  borderRadius: 10,
                  padding: '8px 10px',
                  cursor: 'pointer',
                  color: colors.textSecondary,
                  fontSize: 16,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                👤
              </button>
            </>
          ) : (
            <Web3Login />
          )}
          <button
            onClick={() => setShowSettingsModal(true)}
            style={{
              background: colors.inputBg,
              border: `1px solid ${colors.border}`,
              borderRadius: 10,
              padding: '8px 10px',
              cursor: 'pointer',
              color: colors.textSecondary,
              fontSize: 18,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            ⚙️
          </button>
        </div>
      </div>

      {/* Settings Modal */}
      {showSettingsModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setShowSettingsModal(false)}
        >
          <div
            style={{
              background: colors.contentBg,
              borderRadius: 16,
              padding: 20,
              width: '90%',
              maxWidth: 360,
              maxHeight: '80vh',
              overflowY: 'auto',
              border: `1px solid ${colors.border}`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 20,
              }}
            >
              <h2 style={{ margin: 0, color: colors.text, fontSize: 18 }}>
                {t('settings')}
              </h2>
              <button
                onClick={() => setShowSettingsModal(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: colors.textMuted,
                  fontSize: 20,
                  cursor: 'pointer',
                  padding: 4,
                }}
              >
                ✕
              </button>
            </div>

            {/* Theme Selection */}
            <div style={{ marginBottom: 20 }}>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: colors.textMuted,
                  marginBottom: 10,
                }}
              >
                {t('theme')}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {[
                  { mode: 'system', label: t('themeSystem'), icon: '🖥️' },
                  { mode: 'light', label: t('themeLight'), icon: '☀️' },
                  { mode: 'dark', label: t('themeDark'), icon: '🌙' },
                ].map((item) => (
                  <button
                    key={item.mode}
                    onClick={() => handleThemeModeChange(item.mode)}
                    style={{
                      flex: 1,
                      background: themeMode === item.mode
                        ? 'rgba(102, 126, 234, 0.3)'
                        : colors.cardBg,
                      border: themeMode === item.mode
                        ? '1px solid rgba(102, 126, 234, 0.5)'
                        : `1px solid ${colors.border}`,
                      borderRadius: 10,
                      padding: '10px 8px',
                      cursor: 'pointer',
                      color: themeMode === item.mode ? colors.text : colors.textSecondary,
                      fontSize: 12,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    <span style={{ fontSize: 18 }}>{item.icon}</span>
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Language Selection */}
            <div>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: colors.textMuted,
                  marginBottom: 10,
                }}
              >
                {t('selectLanguage')}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {UI_LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => handleLanguageChange(lang.code)}
                    style={{
                      background: uiLanguage === lang.code
                        ? 'rgba(102, 126, 234, 0.3)'
                        : colors.cardBg,
                      border: uiLanguage === lang.code
                        ? '1px solid rgba(102, 126, 234, 0.5)'
                        : `1px solid ${colors.border}`,
                      borderRadius: 10,
                      padding: '12px 16px',
                      cursor: 'pointer',
                      color: uiLanguage === lang.code ? colors.text : colors.textSecondary,
                      fontSize: 14,
                      textAlign: 'left',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <span>{lang.label}</span>
                    {uiLanguage === lang.code && (
                      <span style={{ color: '#667eea' }}>✓</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filter Toggle + Search + Write Button */}
      <div
        style={{
          width: '100%',
          maxWidth: 430,
          marginBottom: 12,
          display: 'flex',
          gap: 8,
          alignItems: 'center',
        }}
      >
        <button
          onClick={() => setShowFilters(!showFilters)}
          style={{
            background: activeFilterCount > 0 ? 'rgba(102, 126, 234, 0.2)' : colors.inputBg,
            border: activeFilterCount > 0 ? '1px solid rgba(102, 126, 234, 0.5)' : `1px solid ${colors.border}`,
            borderRadius: 10,
            padding: '10px 14px',
            cursor: 'pointer',
            color: activeFilterCount > 0 ? '#667eea' : colors.textSecondary,
            fontSize: 13,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexShrink: 0,
          }}
        >
          <span>{t('filter')}</span>
          {activeFilterCount > 0 && (
            <span
              style={{
                background: '#667eea',
                color: '#fff',
                borderRadius: 10,
                padding: '2px 8px',
                fontSize: 11,
              }}
            >
              {activeFilterCount}
            </span>
          )}
        </button>
        <input
          type="text"
          placeholder={t('search')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            flex: 1,
            background: colors.inputBg,
            border: `1px solid ${colors.border}`,
            borderRadius: 10,
            padding: '10px 14px',
            color: colors.text,
            fontSize: 13,
            outline: 'none',
            minWidth: 0,
          }}
        />
      </div>

      {/* Filters */}
      {showFilters && (
        <div
          style={{
            width: '100%',
            maxWidth: 430,
            background: colors.cardBg,
            border: `1px solid ${colors.border}`,
            borderRadius: 12,
            padding: 16,
            marginBottom: 16,
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          <div style={{ display: 'flex', gap: 10 }}>
            <select
              value={filterServer}
              onChange={(e) => setFilterServer(e.target.value)}
              style={{
                flex: 1,
                background: colors.inputBg,
                border: `1px solid ${colors.border}`,
                borderRadius: 8,
                padding: '10px 12px',
                color: filterServer ? colors.text : colors.textSecondary,
                fontSize: 13,
                outline: 'none',
                cursor: 'pointer',
              }}
            >
              <option value="">{t('allServers')}</option>
              {SERVERS.map((s) => (
                <option key={s} value={s} style={{ background: colors.bg, color: colors.text }}>
                  {s}
                </option>
              ))}
            </select>

            <select
              value={filterBoss}
              onChange={(e) => setFilterBoss(e.target.value)}
              style={{
                flex: 2,
                background: colors.inputBg,
                border: `1px solid ${colors.border}`,
                borderRadius: 8,
                padding: '10px 12px',
                color: filterBoss ? colors.text : colors.textSecondary,
                fontSize: 13,
                outline: 'none',
                cursor: 'pointer',
              }}
            >
              <option value="">{t('allBosses')}</option>
              {BOSSES.map((b) => (
                <option key={b} value={b} style={{ background: colors.bg, color: colors.text }}>
                  {b}
                </option>
              ))}
            </select>
          </div>

          <select
            value={filterLanguage}
            onChange={(e) => setFilterLanguage(e.target.value)}
            style={{
              background: colors.inputBg,
              border: `1px solid ${colors.border}`,
              borderRadius: 8,
              padding: '10px 12px',
              color: filterLanguage ? colors.text : colors.textSecondary,
              fontSize: 13,
              outline: 'none',
              cursor: 'pointer',
            }}
          >
            <option value="">{t('allLanguages')}</option>
            {LANGUAGES.map((l) => (
              <option key={l} value={l} style={{ background: colors.bg, color: colors.text }}>
                {l}
              </option>
            ))}
          </select>

          <button
            onClick={() => setHideRecruited(!hideRecruited)}
            style={{
              background: hideRecruited ? 'rgba(34,197,94,0.15)' : colors.inputBg,
              border: hideRecruited ? '1px solid rgba(34,197,94,0.4)' : `1px solid ${colors.border}`,
              borderRadius: 8,
              padding: '10px 12px',
              cursor: 'pointer',
              color: hideRecruited ? '#22c55e' : colors.textSecondary,
              fontSize: 13,
              fontWeight: hideRecruited ? 600 : 400,
              textAlign: 'left',
            }}
          >
            {hideRecruited ? '✓ ' : ''}모집 완료 글 숨기기
          </button>

          {activeFilterCount > 0 && (
            <button
              onClick={clearFilters}
              style={{
                background: 'rgba(244, 67, 54, 0.15)',
                border: '1px solid rgba(244, 67, 54, 0.4)',
                borderRadius: 8,
                padding: '8px 12px',
                cursor: 'pointer',
                color: '#f44336',
                fontSize: 12,
              }}
            >
              {t('clearFilters')}
            </button>
          )}
        </div>
      )}

      {/* Pinned Notice */}
      {pinnedPost && (
        <div
          onClick={() => navigate(getPath(`/camp/post/${pinnedPost.id}`))}
          style={{
            width: '100%',
            maxWidth: 430,
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
            border: '1px solid rgba(102, 126, 234, 0.4)',
            borderRadius: 12,
            padding: 16,
            marginBottom: 12,
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'linear-gradient(135deg, rgba(102, 126, 234, 0.25) 0%, rgba(118, 75, 162, 0.25) 100%)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)';
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <span
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#fff',
                fontSize: 11,
                fontWeight: 600,
                padding: '3px 8px',
                borderRadius: 6,
              }}
            >
              {t('notice')}
            </span>
          </div>
          <div
            style={{
              fontSize: 16,
              fontWeight: 600,
              color: colors.text,
              marginBottom: 6,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {pinnedPost.title}
          </div>
          <div
            style={{
              fontSize: 13,
              color: colors.textMuted,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <span>{pinnedPost.author_nickname || t('anonymous')}</span>
            <span>{formatDate(pinnedPost.created_at)}</span>
          </div>
        </div>
      )}

      {/* Post List */}
      <div
        style={{
          width: '100%',
          maxWidth: 430,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        {loading ? (
          <div style={{
            textAlign: 'center',
            color: colors.textSecondary,
            padding: 40,
          }}>
            {t('loading')}
          </div>
        ) : filteredPosts.length === 0 ? (
          <div
            style={{
              background: colors.cardBg,
              borderRadius: 16,
              padding: 40,
              textAlign: 'center',
            }}
          >
            <p style={{ color: colors.textSecondary, fontSize: 16, margin: 0 }}>
              {searchQuery ? t('noPostsSearch') : activeFilterCount > 0 ? t('noPostsFilter') : t('noPostsYet')}
            </p>
          </div>
        ) : (
          filteredPosts.map((post) => (
            <div
              key={post.id}
              onClick={() => navigate(getPath(`/camp/post/${post.id}`))}
              style={{
                background: colors.cardBg,
                borderRadius: 12,
                padding: 16,
                cursor: 'pointer',
                transition: 'background 0.2s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = colors.cardHoverBg}
              onMouseLeave={(e) => e.currentTarget.style.background = colors.cardBg}
            >
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 600,
                  color: colors.text,
                  marginBottom: 6,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {post.title}
              </div>
              {(post.server || post.boss || post.member_count) && (() => {
                const recruitSlots = (post.members || []).filter(m => m.type === 'recruit');
                const recruitedClasses = post.recruited_classes || [];
                const allRecruited = recruitSlots.length > 0 && recruitSlots.every(m => recruitedClasses.includes(m.character_class || 'Any Class'));
                return (
                <div
                  style={{
                    fontSize: 12,
                    color: '#667eea',
                    marginBottom: 8,
                    display: 'flex',
                    gap: 8,
                    alignItems: 'center',
                  }}
                >
                  {post.server && <span>[{post.server}]</span>}
                  {post.boss && <span>{post.boss}</span>}
                  {post.member_count && (
                    <span style={{
                      background: 'rgba(102,126,234,0.15)',
                      padding: '1px 6px',
                      borderRadius: 6,
                      fontSize: 11,
                    }}>
                      {post.member_count}P
                    </span>
                  )}
                  {allRecruited && (
                    <span style={{
                      background: 'rgba(34,197,94,0.15)',
                      color: '#22c55e',
                      padding: '1px 6px',
                      borderRadius: 6,
                      fontSize: 11,
                      fontWeight: 600,
                    }}>
                      모집 완료
                    </span>
                  )}
                </div>
                );
              })()}
              <div
                style={{
                  fontSize: 13,
                  color: colors.textMuted,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <span>{post.author_nickname || t('anonymous')}</span>
                <span>{formatDate(post.created_at)}</span>
              </div>
            </div>
          ))
        )}
      </div>
      </div>

      {/* Chat List Modal */}
      <CampChatListModal
        isOpen={isChatListOpen}
        onClose={() => setIsChatListOpen(false)}
      />
    </div>
  );
}
